/**
 * Robust muted-video autoplay for the page's media texture slots
 * (`mediaTextureService`), which feed the vidOS engine.
 *
 * Two browser realities break a naive single `video.play()`:
 *  1. Autoplay policy — mobile browsers (and desktop under some settings) reject
 *     `play()` until the document has seen a user gesture, even for
 *     muted + playsInline video. The rejection is async, so it can't be caught
 *     by feature-detection up front.
 *  2. Readiness races — on a cold cache the element frequently isn't decodable
 *     on the first attempt, so an early `play()` rejects with no retry.
 *
 * Either one leaves a shader frozen on its poster frame. The most visible
 * symptom was feed-card videos never starting on mobile until the user happened
 * to navigate into a main-renderer patch (the tap satisfied the gesture policy)
 * and back. This module automates that recovery: it retries on every readiness
 * event and, as a last resort, on the next user gesture anywhere in the document.
 */

// A single shared one-shot gesture listener for the whole document. Every video
// that has been rejected registers its retry here; the next pointerdown/touchend
// fires them all together, then the listener re-arms only if something registers
// again. Sharing one listener (vs. one per element) means the first gesture
// unblocks every waiting video at once — main renderer and all feed cards.
const pendingGestureRetries = new Set<() => void>();
let gestureListenerArmed = false;

function armGestureRetry(retry: () => void): void {
  pendingGestureRetries.add(retry);
  if (gestureListenerArmed) return;
  gestureListenerArmed = true;
  const fire = () => {
    document.removeEventListener('pointerdown', fire);
    document.removeEventListener('touchend', fire);
    gestureListenerArmed = false;
    // Snapshot then clear before invoking: a retry that rejects again re-arms
    // cleanly into a fresh set rather than mutating the one we're iterating.
    const retries = [...pendingGestureRetries];
    pendingGestureRetries.clear();
    for (const r of retries) r();
  };
  document.addEventListener('pointerdown', fire, { once: true });
  document.addEventListener('touchend', fire, { once: true });
}

/**
 * Attempt to start `videoEl` iff `shouldPlay()` allows it and it is currently
 * paused. A rejected `play()` (autoplay policy / not-yet-decodable) arms a
 * one-shot user-gesture retry. Safe to call repeatedly — a no-op while already
 * running or while gated off — so it can be wired to multiple readiness events.
 */
export function tryPlayVideo(videoEl: HTMLMediaElement, shouldPlay: () => boolean): void {
  if (!shouldPlay() || !videoEl.paused) return;
  const p = videoEl.play();
  if (p && typeof p.catch === 'function') {
    p.catch(() => armGestureRetry(() => tryPlayVideo(videoEl, shouldPlay)));
  }
}

/**
 * Wire robust autoplay onto a video element: retry on each readiness event plus
 * an immediate attempt. Returns a cleanup function that removes the listeners
 * (call it when tearing the element down).
 */
export function attachRobustAutoplay(
  videoEl: HTMLVideoElement,
  shouldPlay: () => boolean,
): () => void {
  // `disposed` is folded into the predicate so a pending first-gesture retry that
  // outlives this element (card unmounted before the user ever tapped) becomes a
  // no-op instead of calling play() on a src-less element and re-arming forever.
  let disposed = false;
  const gatedShouldPlay = () => !disposed && shouldPlay();
  const onReady = () => tryPlayVideo(videoEl, gatedShouldPlay);
  videoEl.addEventListener('canplay', onReady);
  videoEl.addEventListener('loadeddata', onReady);
  tryPlayVideo(videoEl, gatedShouldPlay);
  return () => {
    disposed = true;
    videoEl.removeEventListener('canplay', onReady);
    videoEl.removeEventListener('loadeddata', onReady);
  };
}
